import React from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { FileText, Home, Layers, Search, Upload, X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';

export const AltaAdminDialog = ({
  altaAdminForm,
  getGrupoDe,
  grupos,
  handleAltaAdminTipoChange,
  handleContratoAdminUpload,
  handleContratoBusquedaUpload,
  handleSaveAltaAdmin,
  isAltaAdminOpen,
  savingAltaAdmin,
  setAltaAdminForm,
  setIsAltaAdminOpen,
  sortUnidades,
  unidades,
  unidadesEnGrupo,
  uploadingContratoAdmin,
  uploadingContratoBusqueda,
}) => {
  const toggleBusqueda = (val) =>
    setAltaAdminForm(prev => ({ ...prev, servicio_busqueda_activo: val }));
  const toggleAdmin = (val) =>
    setAltaAdminForm(prev => ({ ...prev, servicio_admin_activo: val }));

  const isGrupo = altaAdminForm.unidad_id.startsWith('grupo_');
  const grupoDeUnidad = !isGrupo ? getGrupoDe(altaAdminForm.unidad_id) : null;
  const usaScopeGrupo = isGrupo || (grupoDeUnidad && altaAdminForm.alcance_grupo === 'todo_grupo');

  return (
      <Dialog open={isAltaAdminOpen} onOpenChange={setIsAltaAdminOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="alta-servicios-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Home className="w-5 h-5 text-green-600" />
              Alta de Servicios
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Marcá los servicios que el propietario está contratando. Son independientes entre sí.
            </p>
          </DialogHeader>
          <form onSubmit={handleSaveAltaAdmin} className="space-y-4">
            {/* Selector de Unidad o Grupo */}
            <div className="space-y-2">
              <Label>Unidad o Grupo *</Label>
              <Select
                value={altaAdminForm.unidad_id}
                onValueChange={(val) => setAltaAdminForm(prev => ({ ...prev, unidad_id: val }))}
              >
                <SelectTrigger data-testid="select-unidad-admin">
                  <SelectValue placeholder="Seleccionar unidad o grupo" />
                </SelectTrigger>
                <SelectContent>
                  {grupos.map(grupo => (
                    <SelectItem key={`grupo_${grupo.grupo_id}`} value={`grupo_${grupo.grupo_id}`}>
                      <div className="flex items-center gap-2">
                        <Layers className="w-4 h-4 text-indigo-500" />
                        <span className="font-medium">{grupo.nombre}</span>
                        <span className="text-xs text-slate-500">({grupo.unidad_ids?.length || 0} uds)</span>
                      </div>
                    </SelectItem>
                  ))}
                  {grupos.length > 0 && (
                    <div className="px-2 py-1 text-xs text-slate-400 border-t mt-1">Unidades</div>
                  )}
                  {sortUnidades(unidades.filter(u => !unidadesEnGrupo.has(u.unidad_id))).map(u => (
                    <SelectItem key={u.unidad_id} value={u.unidad_id}>
                      {u.nombre_unidad} ({u.tipo})
                    </SelectItem>
                  ))}
                  {sortUnidades(unidades.filter(u => unidadesEnGrupo.has(u.unidad_id))).map(u => {
                    const g = getGrupoDe(u.unidad_id);
                    return (
                      <SelectItem key={u.unidad_id} value={u.unidad_id}>
                        <div className="flex items-center gap-1">
                          <span>{u.nombre_unidad}</span>
                          <span className="text-xs text-indigo-500">(en {g?.nombre})</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>

              {altaAdminForm.unidad_id && grupoDeUnidad && !isGrupo && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mt-2 space-y-2">
                  <p className="text-xs font-medium text-indigo-800">
                    Esta unidad pertenece al grupo "{grupoDeUnidad.nombre}"
                  </p>
                  <div className="flex flex-col gap-1.5">
                    <label className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-indigo-100/50">
                      <input
                        type="radio"
                        name="alcance_grupo"
                        checked={altaAdminForm.alcance_grupo === 'solo_unidad'}
                        onChange={() => setAltaAdminForm(prev => ({ ...prev, alcance_grupo: 'solo_unidad' }))}
                        className="w-4 h-4 text-indigo-600"
                      />
                      <span className="text-sm text-indigo-900">Solo esta unidad</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-indigo-100/50">
                      <input
                        type="radio"
                        name="alcance_grupo"
                        checked={altaAdminForm.alcance_grupo === 'todo_grupo'}
                        onChange={() => setAltaAdminForm(prev => ({ ...prev, alcance_grupo: 'todo_grupo' }))}
                        className="w-4 h-4 text-indigo-600"
                      />
                      <span className="text-sm text-indigo-900">
                        Todo el grupo ({grupoDeUnidad.unidad_ids?.length} unidades)
                      </span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* ============ SERVICIO BÚSQUEDA DE INQUILINO ============ */}
            <div className="border border-slate-200 rounded-lg p-4 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="toggle-servicio-busqueda"
                  checked={altaAdminForm.servicio_busqueda_activo}
                  onChange={(e) => toggleBusqueda(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded text-purple-600"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <Search className="w-4 h-4 text-purple-600" />
                    <span className="font-semibold text-slate-900">Búsqueda de inquilino</span>
                  </div>
                  <p className="text-xs text-slate-500">
                    Servicio de búsqueda y selección de inquilino para la unidad.
                  </p>
                </div>
              </label>

              {altaAdminForm.servicio_busqueda_activo && (
                <div className="space-y-2 ml-7">
                  <Label className="text-xs">Contrato de búsqueda de inquilino (opcional)</Label>
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-3">
                    {altaAdminForm.contrato_busqueda_file ? (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-purple-600" />
                          <span className="text-sm text-slate-700 truncate">
                            {altaAdminForm.contrato_busqueda_file.nombre}
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setAltaAdminForm(prev => ({ ...prev, contrato_busqueda_file: null }))}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center gap-1 cursor-pointer">
                        <Upload className="w-6 h-6 text-slate-400" />
                        <span className="text-xs text-slate-500">
                          {uploadingContratoBusqueda ? 'Subiendo...' : 'Subir contrato de búsqueda'}
                        </span>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={handleContratoBusquedaUpload}
                          className="hidden"
                          disabled={uploadingContratoBusqueda}
                          data-testid="upload-contrato-busqueda"
                        />
                      </label>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* ============ SERVICIO ADMINISTRACIÓN MENSUAL ============ */}
            <div className="border border-slate-200 rounded-lg p-4 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="toggle-servicio-admin"
                  checked={altaAdminForm.servicio_admin_activo}
                  onChange={(e) => toggleAdmin(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded text-blue-600"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <Home className="w-4 h-4 text-blue-600" />
                    <span className="font-semibold text-slate-900">Administración mensual</span>
                  </div>
                  <p className="text-xs text-slate-500">
                    Administración del alquiler, cobros y liquidación mensual.
                  </p>
                </div>
              </label>

              {altaAdminForm.servicio_admin_activo && (
                <div className="space-y-3 ml-7">
                  <div className="space-y-2">
                    <Label className="text-xs">Tipo de administración</Label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="tipo_admin"
                          checked={altaAdminForm.tipo_administracion === 'full'}
                          onChange={() => handleAltaAdminTipoChange('full')}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm">Full (20%)</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="tipo_admin"
                          checked={altaAdminForm.tipo_administracion === 'simple'}
                          onChange={() => handleAltaAdminTipoChange('simple')}
                          className="w-4 h-4 text-green-600"
                        />
                        <span className="text-sm">Simple (8%)</span>
                      </label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs">Fee de administración (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      value={altaAdminForm.porcentaje_administracion}
                      onChange={(e) => setAltaAdminForm(prev => ({
                        ...prev,
                        porcentaje_administracion: parseFloat(e.target.value) || 0
                      }))}
                      className="w-24"
                      data-testid="fee-admin-input"
                    />
                  </div>

                  {usaScopeGrupo && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                      <Label className="text-xs text-blue-900 font-medium">Contrato del grupo</Label>
                      <div className="flex flex-col gap-1.5">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="contrato_tipo"
                            checked={altaAdminForm.contrato_tipo === 'mismo'}
                            onChange={() => setAltaAdminForm(prev => ({ ...prev, contrato_tipo: 'mismo' }))}
                            className="w-4 h-4 text-blue-600"
                          />
                          <span className="text-xs">Mismo contrato para todas las unidades</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="contrato_tipo"
                            checked={altaAdminForm.contrato_tipo === 'separados'}
                            onChange={() => setAltaAdminForm(prev => ({ ...prev, contrato_tipo: 'separados', contrato_file: null }))}
                            className="w-4 h-4 text-blue-600"
                          />
                          <span className="text-xs">Contratos separados (cargar luego por unidad)</span>
                        </label>
                      </div>
                    </div>
                  )}

                  {(!usaScopeGrupo || altaAdminForm.contrato_tipo === 'mismo') && (
                    <div className="space-y-2">
                      <Label className="text-xs">Contrato de administración (opcional)</Label>
                      <div className="border-2 border-dashed border-slate-200 rounded-lg p-3">
                        {altaAdminForm.contrato_file ? (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <FileText className="w-5 h-5 text-blue-600" />
                              <span className="text-sm text-slate-700 truncate">
                                {altaAdminForm.contrato_file.nombre}
                              </span>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setAltaAdminForm(prev => ({ ...prev, contrato_file: null }))}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <label className="flex flex-col items-center gap-1 cursor-pointer">
                            <Upload className="w-6 h-6 text-slate-400" />
                            <span className="text-xs text-slate-500">
                              {uploadingContratoAdmin ? 'Subiendo...' : 'Subir contrato de administración'}
                            </span>
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx"
                              onChange={handleContratoAdminUpload}
                              className="hidden"
                              disabled={uploadingContratoAdmin}
                              data-testid="upload-contrato-admin"
                            />
                          </label>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsAltaAdminOpen(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={
                  savingAltaAdmin ||
                  !altaAdminForm.unidad_id ||
                  (!altaAdminForm.servicio_busqueda_activo && !altaAdminForm.servicio_admin_activo)
                }
                data-testid="guardar-alta-admin-btn"
              >
                {savingAltaAdmin ? 'Guardando...' : 'Dar de alta servicios'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
  );
};
